import { Machine } from 'xstate'
import apartmentStates from './realEstateTypeStates/apartmentStates'
import landStates from './realEstateTypeStates/landStates'
import commercialStates from './realEstateTypeStates/commercialStates'

import ibwApartmentStates from './ibwTypeStates/apartmentStates'
import ibwLandStates from './ibwTypeStates/landStates'
import ibwCommercialStates from './ibwTypeStates/commercialStates'

import { ACTION, IBWRealEstateTypeFlow, RealEstateTypeFlow, STEP } from '../@types/state'
import { extendedBuyerLeadPostcodesList, flowMachineActions } from './flowMachineActions'
import { RealEstateType } from '../@types/realEstateType'
import { Intent } from '../@types/intent'
import { Lead } from '../@types/lead'
import { Status } from '../@types/status'
import { WizardFlowType } from '../@types/wizardFlowType'
import { IntegrationType, TealiumProfileFromServer } from '../@types/config'
import { User } from '../@types/user'
import { Address } from '../@types/address'
import { FeatureName, getFeatureToggleValue } from '../featureToggles/featureToggles'
import solarFlowStates from './solarFlowTypeStates/solarFlowStates'
import { TimeHorizon } from '../@types/timeHorizon'
import { Ownership } from '../@types/ownership'

const isRealEstateType = (ctx: FlowMachineContext, realEstateType: RealEstateType): boolean =>
  ctx.lead != undefined && ctx.lead.realEstateType == realEstateType

export interface FlowMachineContext {
  skippedSteps?: STEP[]
  lead?: Lead
  location?: { status?: Status; postcode?: string }
  richReportSent?: boolean
  wizardFlowType?: WizardFlowType
  tealium?: boolean
  optionalTealiumProfile?: TealiumProfileFromServer
  integrationType?: IntegrationType
  user?: User
  address?: Address
}

const tealium = window.IS24?.RLE?.configuration?.tealium
const integrationType = window.IS24?.RLE?.configuration?.integrationType

const flowMachine = Machine<FlowMachineContext>(
  {
    id: 'RLE',
    initial: STEP.FLOW_SETUP,
    context: {
      skippedSteps: [],
      tealium,
      integrationType,
    },
    states: {
      [STEP.FLOW_SETUP]: {
        on: {
          [ACTION.NEXT]: [
            {
              target: STEP.SOLAR,
              cond: 'isSolarflow',
            },
            {
              target: STEP.IBW_REAL_ESTATE_TYPE,
              cond: 'isIbwFlow',
            },
            {
              target: STEP.REAL_ESTATE_TYPE,
            },
          ],
        },
        exit: ['updateContextFromQueryParam'],
      },
      [STEP.SOLAR]: {
        ...solarFlowStates,
      },
      [STEP.REAL_ESTATE_TYPE]: {
        entry: ['enterOrSkipRealEstateType'],
        on: {
          [ACTION.NEXT]: [
            { target: RealEstateTypeFlow.APARTMENT, cond: 'isApartment' },
            { target: RealEstateTypeFlow.HOUSE, cond: 'isHouse' },
            { target: RealEstateTypeFlow.LAND, cond: 'isLand' },
            { target: RealEstateTypeFlow.COMMERCIAL, cond: 'isCommercial' },
          ],
        },
      },
      [STEP.IBW_REAL_ESTATE_TYPE]: {
        entry: ['enterOrSkipRealEstateType'],
        on: {
          [ACTION.NEXT]: [
            { target: IBWRealEstateTypeFlow.APARTMENT, cond: 'isApartment' },
            { target: IBWRealEstateTypeFlow.HOUSE, cond: 'isHouse' },
            { target: IBWRealEstateTypeFlow.LAND, cond: 'isLand' },
            { target: IBWRealEstateTypeFlow.COMMERCIAL, cond: 'isCommercial' },
          ],
        },
      },
      [RealEstateTypeFlow.APARTMENT]: {
        ...apartmentStates,
      },
      [RealEstateTypeFlow.HOUSE]: {
        ...apartmentStates,
      },
      [RealEstateTypeFlow.LAND]: {
        ...landStates,
      },
      [RealEstateTypeFlow.COMMERCIAL]: {
        ...commercialStates,
      },
      [IBWRealEstateTypeFlow.APARTMENT]: {
        ...ibwApartmentStates,
      },
      [IBWRealEstateTypeFlow.HOUSE]: {
        ...ibwApartmentStates,
      },
      [IBWRealEstateTypeFlow.COMMERCIAL]: {
        ...ibwCommercialStates,
      },
      [IBWRealEstateTypeFlow.LAND]: {
        ...ibwLandStates,
      },
    },
  },
  {
    actions: {
      ...flowMachineActions,
    },
    guards: {
      isIbwFlow: (ctx) => ctx.wizardFlowType === WizardFlowType.IBW,
      isRleFlow: (ctx) => ctx.wizardFlowType === WizardFlowType.RLE,
      isSolarflow: (ctx) => ctx.wizardFlowType === WizardFlowType.SOLAR,
      isApartment: (ctx) => isRealEstateType(ctx, RealEstateType.APARTMENT),
      isHouse: (ctx) => isRealEstateType(ctx, RealEstateType.HOUSE),
      isCommercial: (ctx) => isRealEstateType(ctx, RealEstateType.COMMERCIAL),
      isLand: (ctx) => isRealEstateType(ctx, RealEstateType.LAND),
      isExtendedBuyerLeadOverview: (ctx) =>
        getFeatureToggleValue(FeatureName.EXTENDED_BUYER_LEADS_OVERVIEW) && (ctx.lead?.extendedBuyerLead ?? false),
      isExtendedBuyerLead: (ctx) => ctx.lead?.extendedBuyerLead != undefined && ctx.lead.extendedBuyerLead,
      isSell: (ctx) =>
        ctx.lead?.intent != undefined &&
        [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE, Intent.INDEPENDENT_SALE].includes(ctx.lead.intent),
      isRentOutIntent: (ctx) =>
        getFeatureToggleValue(FeatureName.MOOVIN_INTEGRATION) &&
        ctx.lead?.intent != undefined &&
        Intent.LET === ctx.lead.intent,
      isRentOutWithoutRealtor: (ctx) =>
        getFeatureToggleValue(FeatureName.MOOVIN_INTEGRATION) &&
        ctx.lead?.intent != undefined &&
        [Intent.INDEPENDENT_LET].includes(ctx.lead.intent),
      isRentOutReinforcement: (ctx) =>
        getFeatureToggleValue(FeatureName.MOOVIN_INTEGRATION) &&
        ctx.lead?.intent != undefined &&
        [Intent.INDEPENDENT_LET, Intent.MAKLER_LET, Intent.MAYBE_MAKLER_LET].includes(ctx.lead.intent),
      isSellWithRealtor: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        [Intent.MAKLER_SALE, Intent.MAYBE_MAKLER_SALE].includes(ctx.lead.intent),
      isSellWithoutRealtor: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        [Intent.INDEPENDENT_SALE].includes(ctx.lead.intent) &&
        !getFeatureToggleValue(FeatureName.HIDE_DEVCOM_PARTIAL_SALE),
      isNonHappyPath: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        // not LAND, not LET
        !(
          ctx.lead?.realEstateType == RealEstateType.LAND &&
          [Intent.LET, Intent.INDEPENDENT_LET].includes(ctx.lead?.intent)
        ) &&
        // if is INDEPENDENT_SALE, BUY, RENT OR LET OR LET WITHOUT REALTOR
        (([Intent.BUY, Intent.RENT, Intent.LET].includes(ctx.lead.intent) &&
          ctx.lead?.realEstateType != RealEstateType.COMMERCIAL) ||
          // OR: COMMERCIAL MAKLER SALE AND NOT EXTENDEDBUYERLEAD
          (ctx.lead?.realEstateType == RealEstateType.COMMERCIAL && ctx.lead?.intent == Intent.MAKLER_SALE)) &&
        !ctx.lead.extendedBuyerLead,
      isNonHappyPathLand: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        //  LAND AND INDEPENDENT_SALE
        ctx.lead?.realEstateType == RealEstateType.LAND &&
        (ctx.lead?.intent == Intent.INDEPENDENT_SALE || ctx.lead?.intent == Intent.BUY) &&
        !ctx.lead.extendedBuyerLead,
      isValuationPdfAndBuyLetOrRent: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead.intent != undefined &&
        [
          Intent.BUY,
          Intent.LET,
          Intent.RENT,
          Intent.INDEPENDENT_SALE,
          Intent.INDEPENDENT_WITH_OFFER_SALE,
          Intent.INDEPENDENT_LET,
          Intent.MAYBE_MAKLER_LET,
          Intent.MAKLER_LET,
        ].includes(ctx.lead.intent),
      isValuePropositionStep: (ctx) =>
        ctx.lead != undefined &&
        ctx.lead.intent != undefined &&
        ctx.lead.intent === Intent.BUY &&
        extendedBuyerLeadPostcodesList(ctx),
      isConsultationScreen: (ctx) =>
        getFeatureToggleValue(FeatureName.IS_SELL_WITHOUT_REALTOR) &&
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        ctx.lead.realEstateType != undefined &&
        [RealEstateType.HOUSE, RealEstateType.APARTMENT, RealEstateType.LAND].includes(ctx.lead.realEstateType) &&
        ctx.lead.intent === Intent.INDEPENDENT_SALE &&
        ctx.lead.timeHorizon != undefined &&
        [TimeHorizon.IMMEDIATELY, TimeHorizon.NEXT_SIX_MONTHS].includes(ctx.lead.timeHorizon) &&
        ctx.lead?.ownership != undefined &&
        [Ownership.YES, Ownership.CO_OWNER].includes(ctx.lead.ownership),
      isDisableContactStep: (ctx) =>
        getFeatureToggleValue(FeatureName.REMOVE_CONTACT_SCREEN) &&
        ctx.lead != undefined &&
        ctx.lead?.intent != undefined &&
        ctx.lead.realEstateType != undefined &&
        [RealEstateType.HOUSE, RealEstateType.APARTMENT].includes(ctx.lead.realEstateType) &&
        ctx.lead.intent != undefined &&
        [Intent.INDEPENDENT_LET, Intent.INDEPENDENT_SALE].includes(ctx.lead.intent) &&
        !ctx.lead.isConsultationAccepted,
    },
  }
)

export default flowMachine
